import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import React from "react";
import { RouteComponentProps } from "react-router";

import {
  useDeviceTypes,
  PremiumStaysInfoAccordian,
} from "halifax";
import { Skeleton } from "@material-ui/lab";
import {
  AUTOCOMPLETE_LOCATION_SELECTED,
  AutocompleteLocationSelectedProperties,
  CALENDAR_DATES_SELECTED,
  CalendarDatesSelectedProperties,
  IResult,
  LAUNCHED_MOBILE_LOCATION_SCREEN,
  LaunchedMobileLocationScreenProperties,
  StayTypesEnum,
  VIEWED_PREMIUM_SEARCH,
} from "redmond";

import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  LC_GATING_EXPERIMENT,
  PREMIER_COLLECTION_EXPERIMENT,
  useExperiments,
  VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { PATH_HOME, PATH_VACATION_RENTALS } from "../../utils/paths";

import { MobilePremierCollectionSearchControlV3 } from "./components/MobilePremierCollectionSearchControlV3";
import { PremierCollectionSearchControl } from "./components/PremierCollectionSearchControl";
import { PremierCollectionLandingPageBenefits } from "./components/PremierCollectionLandingPageBenefits";
import { LuxQ2VROfferModal } from "./components/LuxQ2VROfferModal/component";
import { PremierCollectionSearchConnectorProps } from "./container";
import * as textConstants from "./textConstants";

import "./styles.scss";

export interface IPremierCollectionSearchProps
  extends PremierCollectionSearchConnectorProps,
    RouteComponentProps {}

export const PremierCollectionSearch = (
  props: IPremierCollectionSearchProps,
) => {
  const {
    fetchMegaMenuRegions,
    fetchRewardsAccounts,
    largestValueAccount,
    stayType,
    setStayType,
    history,
    listPaymentMethods,
    isVentureX,
    fetchTravelWalletDetails,
    travelWalletOffers,
  } = props;
  const { matchesMobile } = useDeviceTypes();

  const expState = useExperiments();

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT,
    ) === AVAILABLE;

  const isLifestyleOnlyCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
      AVAILABLE && !isPremierCollectionEnabled;

  const VRForPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT,
  );
  const isVRForPremiumCardHoldersEnabled = React.useMemo(
    () => VRForPremiumCardHolders === AVAILABLE,
    [VRForPremiumCardHolders],
  );

  const VRForNonPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  );
  const isVRForNonPremiumCardHoldersEnabled = React.useMemo(
    () => VRForNonPremiumCardHolders === AVAILABLE,
    [VRForNonPremiumCardHolders],
  );

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const shouldBeVacationRentals = React.useMemo(() => {
    return (
      history?.location.pathname === PATH_VACATION_RENTALS &&
      (isVRForPremiumCardHoldersEnabled || isVRForNonPremiumCardHoldersEnabled)
    );
  }, [
    history?.location.pathname,
    isVRForPremiumCardHoldersEnabled,
    isVRForNonPremiumCardHoldersEnabled,
  ]);

  React.useEffect(() => {
    if (expState.experiments.length > 0) {
      if (
        history?.location.pathname === PATH_VACATION_RENTALS &&
        !isVRForPremiumCardHoldersEnabled &&
        !isVRForNonPremiumCardHoldersEnabled
      ) {
        history.push(PATH_HOME);
      }

      if (shouldBeVacationRentals) {
        setStayType(StayTypesEnum.VacationRentals);
      } else {
        setStayType(StayTypesEnum.Hotels);
      }
    }
  }, [expState, history, history?.location.pathname, shouldBeVacationRentals]);

  React.useEffect(() => {
    fetchRewardsAccounts();
    listPaymentMethods();
    fetchTravelWalletDetails();
    setTimeout(() => window.scrollTo(0, 0), 0);

    trackEvent({
      eventName: VIEWED_PREMIUM_SEARCH,
      properties: {},
    });
  }, []);

  React.useEffect(() => {
    if (matchesMobile) {
      const properties: LaunchedMobileLocationScreenProperties = {
        funnel: "premier_collection",
        url: window.location.pathname,
      };
      trackEvent({ eventName: LAUNCHED_MOBILE_LOCATION_SCREEN, properties });
    }
  }, [matchesMobile]);

  React.useEffect(() => {
    fetchMegaMenuRegions();
  }, []);

  const handleSelectLocation = React.useCallback((value: IResult | null) => {
    if (value?.label) {
      const properties: AutocompleteLocationSelectedProperties = {
        funnel: "premier_collection",
        url: window.location.pathname,
        autocomplete_value: value.label,
      };
      trackEvent({ eventName: AUTOCOMPLETE_LOCATION_SELECTED, properties });
    }
  }, []);

  const handleSelectDates = React.useCallback(
    (fromDate: Date | null, untilDate: Date | null) => {
      if (fromDate && untilDate) {
        const properties: CalendarDatesSelectedProperties = {
          funnel: "premier_collection",
          url: window.location.pathname,
          start_date: dayjs(fromDate).format("YYYY-MM-DD"),
          end_date: dayjs(untilDate).format("YYYY-MM-DD"),
        };
        trackEvent({ eventName: CALENDAR_DATES_SELECTED, properties });
      }
    },
    [],
  );

  const showTitleSkeleton =
    (isVRForPremiumCardHoldersEnabled ||
      isVRForNonPremiumCardHoldersEnabled ||
      expState.experiments.length === 0) &&
    !largestValueAccount;

  if (matchesMobile) {
    return (
      <Box className="mobile-premier-collection-search-root">
        <Box className="premier-collection-search-container-mobile">
          <MobilePremierCollectionSearchControlV3
            onSelectLocation={handleSelectLocation}
            onSelectDates={handleSelectDates}
            isLifestyleCollection={isLifestyleOnlyCollectionEnabled}
            includesLifestyleCollection={isPremierCollectionEnabled}
            includesVacationRentals={
              isVRForPremiumCardHoldersEnabled ||
              isVRForNonPremiumCardHoldersEnabled
            }
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={clsx("premier-collection-search-root", {
        "premium-stays": isPremierCollectionEnabled,
        "lifestyle-collection": isLifestyleOnlyCollectionEnabled,
        "with-vacation-rentals":
          isVRForPremiumCardHoldersEnabled ||
          isVRForNonPremiumCardHoldersEnabled,
        "premium-vacation-rentals":
          stayType === StayTypesEnum.VacationRentals &&
          (isVRForPremiumCardHoldersEnabled ||
            isVRForNonPremiumCardHoldersEnabled),
        "non-premium-vacation-rentals":
          stayType === StayTypesEnum.VacationRentals &&
          isVRForNonPremiumCardHoldersEnabled,
        xmod: isXmodRedesign,
      })}
    >
      <LuxQ2VROfferModal
        shouldBeVacationRentals={shouldBeVacationRentals}
        travelWalletOffers={travelWalletOffers}
      />
      <Box className="premier-collection-search-top-section">
        <Box className="premier-collection-search-container-desktop">
          {showTitleSkeleton ? (
            <Skeleton className="search-title-skeleton" aria-hidden="true" />
          ) : (
            <Typography variant="h2" className="search-title">
              {((isVRForPremiumCardHoldersEnabled ||
                isVRForNonPremiumCardHoldersEnabled) &&
                largestValueAccount &&
                textConstants.TITLE_WITH_EARN(
                  stayType === StayTypesEnum.VacationRentals
                    ? 5
                    : largestValueAccount?.earn.hotelsMultiplier,
                  largestValueAccount?.productDisplayName,
                  stayType === StayTypesEnum.VacationRentals,
                )) ||
                (isLifestyleOnlyCollectionEnabled &&
                  textConstants.LC_FOR_NON_PREMIUM_CARDHOLDERS_TITLE) ||
                (isPremierCollectionEnabled &&
                  textConstants.LC_FOR_PREMIUM_CARDHOLDERS_TITLE) ||
                textConstants.TITLE_FIRST_HALF}
            </Typography>
          )}
          <Typography className="search-subtitle">
            {(isVRForNonPremiumCardHoldersEnabled &&
              stayType === StayTypesEnum.VacationRentals &&
              textConstants.VR_SUBTITLE) ||
              (isLifestyleOnlyCollectionEnabled &&
                textConstants.LC_FOR_NON_PREMIUM_CARDHOLDERS_SUBTITLE) ||
              (isPremierCollectionEnabled &&
                textConstants.LC_FOR_PREMIUM_CARDHOLDERS_SUBTITLE) ||
              textConstants.SUBTITLE}
          </Typography>

          <PremierCollectionSearchControl
            showMegaMenu={true}
            includesLifestyleCollection={
              isPremierCollectionEnabled || isLifestyleOnlyCollectionEnabled
            }
            isLifestyleCollection={isLifestyleOnlyCollectionEnabled}
            includesVacationRentals={
              isVRForPremiumCardHoldersEnabled ||
              isVRForNonPremiumCardHoldersEnabled
            }
          />
        </Box>
      </Box>
      <Box className="landing-page-vibes-refresh-container">
        <PremierCollectionLandingPageBenefits
          isVentureX={isVentureX}
          stayType={stayType}
          travelWalletOffers={travelWalletOffers}
        />

        <Box className="landing-page-accordion-wrapper">
          <PremiumStaysInfoAccordian
            variant="with-lifestyle-collection"
            includesVacationRentals={
              isVRForPremiumCardHoldersEnabled ||
              isVRForNonPremiumCardHoldersEnabled
            }
            isMobile={matchesMobile}
            defaultClosed={
              isVRForPremiumCardHoldersEnabled ||
              isVRForNonPremiumCardHoldersEnabled
            }
            stayType={stayType}
            isXmodRedesign={isXmodRedesign}
            useChevronIcons
          />
        </Box>
      </Box>
    </Box>
  );
};
